import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REDO_STORAGE_KEY = 'redo_coverage_enabled';

export function useRedoCoverage(cartItems = [], cartTotal = 0) {
  const [coverage, setCoverage] = useState({
    eligible: false,
    price: 0,
    currency: 'USD',
    productTitle: '',
    variantId: '',
    productId: '',
    imageUrl: '',
    loading: true,
  });
  const [enabled, setEnabled] = useState(() => {
    return localStorage.getItem(REDO_STORAGE_KEY) === 'true';
  });

  const fetchCoverage = useCallback(async () => {
    if (!cartItems.length || cartTotal <= 0) {
      setCoverage(prev => ({ ...prev, eligible: false, loading: false }));
      return;
    }

    try {
      const items = cartItems.map(item => ({
        product_id: item.product_id,
        shopify_variant_id: item.shopify_variant_id || item.variant_id || '',
        shopify_product_id: item.shopify_product_id || '',
        price: item.price,
        quantity: item.quantity,
      }));

      const resp = await fetch(`${API_URL}/api/redo/coverage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, total: cartTotal, currency: 'USD' }),
      });

      if (!resp.ok) throw new Error('Coverage fetch failed');
      const data = await resp.json();

      setCoverage({
        eligible: data.eligible,
        price: data.price || 0,
        currency: data.currency || 'USD',
        productTitle: data.product_title || 'Free returns on your order',
        variantId: data.variant_id || '',
        productId: data.product_id || '',
        imageUrl: data.image_url || '',
        loading: false,
      });
    } catch {
      setCoverage(prev => ({ ...prev, eligible: false, loading: false }));
    }
  }, [cartItems.length, cartTotal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCoverage(); }, [fetchCoverage]);
  // Note: fetchCoverage intentionally keys off cartItems.length (not the array identity)
  // to avoid refetching coverage on every render when a new array reference is passed.

  const toggle = useCallback((value) => {
    const newVal = typeof value === 'boolean' ? value : !enabled;
    setEnabled(newVal);
    localStorage.setItem(REDO_STORAGE_KEY, String(newVal));
  }, [enabled]);

  const totalWithCoverage = enabled && coverage.eligible
    ? cartTotal + coverage.price
    : cartTotal;

  return {
    ...coverage,
    enabled,
    toggle,
    totalWithCoverage,
  };
}
